import { useMemo } from 'react';
import Input from '~/components2/forms/Input';
import Select, { type Item as SelectItem } from '~/components2/forms/Select';
import {
  type AdvancedFilterColumnDef,
  DateSelect,
  FilterOperator,
  isEmptyStateOperator,
  RemoveFilter,
  type TableFilterPropsBase,
} from '../common';
import store from '../store';
import type FilterRule from './FilterRule';

const operatorLabels: { [operator in FilterOperator]: string } = {
  [FilterOperator.Includes]: 'Includes',
  [FilterOperator.DoesNotInclude]: 'Does Not Include',
  [FilterOperator.IsEqualTo]: 'Is Equal To',
  [FilterOperator.IsNotEqualTo]: 'Is Not Equal To',
  [FilterOperator.IsEmpty]: 'Is Empty',
  [FilterOperator.IsNotEmpty]: 'Is Not Empty',
  [FilterOperator.IsBefore]: 'Before Date',
  [FilterOperator.IsAfter]: 'After Date',
  [FilterOperator.IsTrue]: 'Is True',
  [FilterOperator.IsFalse]: 'Is False',
  [FilterOperator.IsNull]: 'Is Null',
};

const operators = {
  text: [
    FilterOperator.Includes,
    FilterOperator.DoesNotInclude,
    FilterOperator.IsEqualTo,
    FilterOperator.IsNotEqualTo,
    FilterOperator.IsEmpty,
    FilterOperator.IsNotEmpty,
  ],
  boolean: [FilterOperator.IsTrue, FilterOperator.IsFalse],
  select: [FilterOperator.IsEqualTo, FilterOperator.IsNotEqualTo],
  date: [FilterOperator.IsBefore, FilterOperator.IsAfter, FilterOperator.IsNotEmpty, FilterOperator.IsNull],
};

const getOperatorLabels = (arr: FilterOperator[]): SelectItem[] => {
  return arr.map((value) => ({
    value,
    label: <div className="text-xs">{operatorLabels[value]}</div>,
  }));
};

const booleanItems = getOperatorLabels(operators.boolean);
const selectItems = getOperatorLabels(operators.select);
const dateItems = getOperatorLabels(operators.date);
const textItems = getOperatorLabels(operators.text);

const getFilter = (id: string) => {
  return store.filtersById[id] as FilterRule;
};

export interface RuleProps<T> extends TableFilterPropsBase<T> {
  rule: FilterRule;
}

export default function Rule<T>({ table, columns, parentId, rule }: RuleProps<T>) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const columnItems = useMemo(() => {
    return columns.map(({ id }) => {
      const def = table.getColumn(id)?.columnDef.header?.toString();
      return {
        value: id,
        label: <div className="text-xs">{def || 'UNKNOWN'}</div>,
      };
    });
  }, [columns]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const currentColumn = useMemo(() => {
    const c = columns.find((c) => c.id === rule.columnId);
    if (c) {
      const t = c?.type;
      if (operators[t]) {
        const ops = operators[t];
        if (!ops.includes(rule.operator)) {
          getFilter(rule.id).setOperator(ops[0]);
        }
      }
    }

    return c;
  }, [rule.columnId]);

  if (!parentId || !currentColumn) return null;

  return (
    <div className="flex w-full justify-start space-x-2 text-sm">
      <div className="w-[10rem]">
        <Select
          label=""
          value={rule.columnId}
          items={columnItems}
          onChange={(e) => {
            if (!e) return;
            const v = e as string;
            const r = getFilter(rule.id);
            r.setColumnId(v);
          }}
          popoverTriggerClassName="w-full"
        />
      </div>

      <FilterValue table={table} rule={rule} column={currentColumn} />

      <RemoveFilter table={table} columns={columns} filterId={rule.id} parentId={parentId} />
    </div>
  );
}

function FilterValue<T>({
  table,
  rule,
  column,
}: Pick<RuleProps<T>, 'table' | 'rule'> & { column: AdvancedFilterColumnDef }) {
  const { operator, columnId } = rule;
  const { type } = column;

  const def = table.getColumn(columnId)?.columnDef.header?.toString() || columnId;

  const onOperatorChange = (e: string | number | null) => {
    if (!e) return;
    const v = e as FilterOperator;
    const r = getFilter(rule.id);
    r.setOperator(v);
  };

  if (type === 'boolean') {
    return (
      <div className="grow">
        <Select
          label=""
          value={operator}
          items={booleanItems}
          onChange={onOperatorChange}
          popoverTriggerClassName="w-full"
        />
      </div>
    );
  }

  if (type === 'select') {
    return (
      <>
        <div className="w-[10rem]">
          <Select
            label=""
            value={operator}
            items={selectItems}
            onChange={onOperatorChange}
            popoverTriggerClassName="w-full"
          />
        </div>

        <div className="grow">
          <Select
            label=""
            value={rule.value?.toString()}
            items={column.items}
            onChange={(e) => {
              getFilter(rule.id).setValue(e?.toString() || '');
            }}
            popoverTriggerClassName="w-full"
          />
        </div>
      </>
    );
  }

  if (type === 'date') {
    if (isEmptyStateOperator(operator)) {
      return (
        <div className="grow">
          <Select
            label=""
            value={operator}
            items={dateItems}
            onChange={onOperatorChange}
            popoverTriggerClassName="w-full"
          />
        </div>
      );
    }
    return (
      <>
        <div className="w-[10rem]">
          <Select
            label=""
            value={operator}
            items={dateItems}
            defaultValue={dateItems[0].value}
            onChange={onOperatorChange}
            popoverTriggerClassName="w-full"
          />
        </div>

        <DateSelect rule={rule} />
      </>
    );
  }

  return (
    <>
      <div className="w-[10rem]">
        <Select
          label=""
          value={operator}
          items={textItems}
          onChange={onOperatorChange}
          popoverTriggerClassName="w-full"
        />
      </div>

      <div className="grow">
        <Input
          label=""
          placeholder={`Search ${def}`}
          value={rule.value?.toString() ?? ''}
          onChange={(e) => {
            getFilter(rule.id).setValue(e);
          }}
          className="w-full bg-white!"
        />
      </div>
    </>
  );
}
